import { createContext, useContext, useState } from 'react';
import type { EnrichedToothImage } from '@/hooks/useToothImages';

// ── Context shape ──────────────────────────────────────────────────────────────

interface DentalOverlayContextValue {
  pinnedImage: EnrichedToothImage | null;
  setPinnedImage: (image: EnrichedToothImage | null) => void;
  opacity: number; // 0..1
  setOpacity: (opacity: number) => void;
}

const DEFAULT_OVERLAY: DentalOverlayContextValue = {
  pinnedImage: null,
  setPinnedImage: () => {},
  opacity: 0.5,
  setOpacity: () => {},
};

const DentalOverlayContext = createContext<DentalOverlayContextValue>(DEFAULT_OVERLAY);

// ── Provider ──────────────────────────────────────────────────────────────────

export function DentalOverlayProvider({ children }: { children: React.ReactNode }) {
  const [pinnedImage, setPinnedImage] = useState<EnrichedToothImage | null>(null);
  const [opacity, setOpacity] = useState(0.5);

  return (
    <DentalOverlayContext.Provider value={{ pinnedImage, setPinnedImage, opacity, setOpacity }}>
      {children}
    </DentalOverlayContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

/** Safe to call outside a provider — returns no-op defaults when no provider present. */
export function useDentalOverlay(): DentalOverlayContextValue {
  return useContext(DentalOverlayContext);
}
